/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { LayerManager } from '@appCanvasCommon/layers/LayerManager';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';

/**
 * LayerService
 * If not used, this can be ripped out.
 * The intention for this service was to replace the layer management that the old `Hud.ts` file was handling.
 * With the transition to the canvas scene graph, this may not be needed anymore, however the option was made available regardless.
 */
@Injectable({
	providedIn: 'root',
})
export class LayerService implements OnDestroy {
	/**
	 * Subject used for cleanup when the service is destroyed.
	 */
	private _shutdownSubject$: Subject<void> = new Subject<void>();
	/**
	 * Reference to the LayerManager instance that handles the canvas scene graph.
	 * This is initialized after the canvas is fully loaded.
	 */
	private _layerManager: LayerManager;
	/**
	 * Getter that provides safe access to the LayerManager instance.
	 * Throws an error if accessed before canvas is loaded (CANVAS_LOADED event).
	 * @throws Error if accessed before LayerManager is initialized
	 * @returns The initialized LayerManager instance
	 */
	get layerManager(): LayerManager {
		if (!this._layerManager) {
			throw new Error(
				`LayerService: Tried to access the layerManager before it was initialized. The LayerManager is initialized after the StateEvents.CANVAS_LOADED event is dispatched.`
			);
		}

		return this._layerManager;
	}
	/**
	 * Registers the LayerManager instance with the service after canvas loads (CANVAS_LOADED state event).
	 * @param layerManager - The LayerManager instance from the canvas renderer
	 */
	public registerPlanData(layerManager: LayerManager): void {
		this._layerManager = layerManager;
	}
	/**
	 * Creates a new layer in the scene graph at the specified path.
	 * Delegates to the LayerManager's addLayer method.
	 * @param path - The path where the new layer should be created (Example: ui/timeline or ui/timeline/tickets)
	 * @returns A new FancyContainer representing the created layer
	 */
	public addLayer(path: string): FancyContainer {
		return this.layerManager.addLayer(path);
	}
	/**
	 * Retrieves a layer from the scene graph at the specified path.
	 * Returns null if the layer doesn't exist.
	 * @param path - The path to the desired layer (Example: ui/timeline or ui/timeline/tickets)
	 * @returns The FancyContainer at the specified path, or null if not found
	 */
	public getLayer(path: string): FancyContainer | null {
		return this.layerManager.getLayer(path);
	}
	/**
	 * Removes a layer at the specified path
	 * Delegates to the LayerManager's addLayer method.
	 * @param path The path to the layer to remove
	 */
	public removeLayer(path: string): void {
		this.layerManager.removeLayer(path);
	}

	ngOnDestroy() {
		this._shutdownSubject$.next();
		this._layerManager = null;
	}
}
